// URL-safe base64 helpers used by the URL-state encoder.
//
// We use a URL-safe variant (RFC 4648 §5) that replaces `+/` with `-_` and
// strips trailing `=` padding so the output survives copy-paste through
// chat tools, email, and anything that treats `+` as a space.

const STD_TO_URL = /[+/]/g;
const URL_TO_STD = /[-_]/g;
const URL_PAD_NEEDED: Record<number, string> = { 0: '', 2: '==', 3: '=' };

function stdToUrl(c: string): string {
  return c === '+' ? '-' : '_';
}
function urlToStd(c: string): string {
  return c === '-' ? '+' : '/';
}

/**
 * Encode a UTF-8 string to URL-safe base64 (no padding).
 *
 * Works in browsers (TextEncoder + btoa) and Node 20+ (Buffer fallback).
 */
export function encodeUtf8ToUrlBase64(input: string): string {
  let std: string;
  if (typeof globalThis.btoa === 'function') {
    const bytes = new TextEncoder().encode(input);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) {
      bin += String.fromCharCode(bytes[i]!);
    }
    std = globalThis.btoa(bin);
  } else {
    // Node fallback (used by vitest in CI; no DOM globals).
    std = Buffer.from(input, 'utf-8').toString('base64');
  }
  return std.replace(STD_TO_URL, stdToUrl).replace(/=+$/, '');
}

/**
 * Decode URL-safe base64 (no padding) back to a UTF-8 string. Returns null on
 * any malformed input rather than throwing — the URL-state decoder uses null
 * to mean "fall back to default catalog view".
 */
export function decodeUrlBase64ToUtf8(input: string): string | null {
  if (typeof input !== 'string' || input.length === 0) return null;
  const std = input.replace(URL_TO_STD, urlToStd);
  const padNeeded = URL_PAD_NEEDED[std.length % 4];
  if (padNeeded === undefined) return null;
  const padded = std + padNeeded;
  try {
    if (typeof globalThis.atob === 'function') {
      const bin = globalThis.atob(padded);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    }
    return Buffer.from(padded, 'base64').toString('utf-8');
  } catch {
    return null;
  }
}

/** Byte length of the UTF-8 representation. */
export function utf8ByteLength(s: string): number {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length;
  return Buffer.byteLength(s, 'utf-8');
}
